import { Sheet, SheetContent, SheetTitle } from "@/shared/ui/sheet";
import type { Asset } from "@/features/assets/hooks/usePropertyAssetRegistry";
import { cn } from "@/shared/lib/utils";
import { useEffect, useState } from "react";

import { getQrCodeImageUrl } from "@/features/assets/services/qrCodeUrl";

import { AssetDetailContent } from "./assetDetailPanel/AssetDetailContent";
import { AssetHeroHeader } from "./assetDetailPanel/AssetHeroHeader";

interface AssetDetailPanelProps {
  asset: Asset | null;
  isOpen: boolean;
  onClose: () => void;
}

export function AssetDetailPanel({ asset, isOpen, onClose }: AssetDetailPanelProps) {
  const [isTransitioning, setIsTransitioning] = useState(false);

  const qrImageUrl = getQrCodeImageUrl(asset?.qr_code_id);

  // Handle asset switching with smooth content transitions
  useEffect(() => {
    if (!asset || !isOpen) return;

    setIsTransitioning(true);

    requestAnimationFrame(() => {
      const scrollArea = document.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollArea) {
        scrollArea.scrollTop = 0;
      }
    });

    const timer = setTimeout(() => setIsTransitioning(false), 150);
    return () => clearTimeout(timer);
  }, [asset?.id, isOpen]);

  if (!asset) return null;

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent
        side="right"
        className="w-1/2 max-w-none p-0 flex flex-col overflow-hidden"
      >
        <SheetTitle className="sr-only">{asset.name}</SheetTitle>
        <AssetHeroHeader asset={asset} qrImageUrl={qrImageUrl} isTransitioning={isTransitioning} />

        <div className="flex-1 overflow-auto p-6">
          <div className={cn(
            "transition-opacity duration-150",
            isTransitioning && "opacity-0"
          )}>
            <AssetDetailContent asset={asset} enabled={isOpen} />
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
